function parseToFloat(value) {
  const cleanedValue = typeof value === 'string'
    ? value.replace(/,/g, '')
    : value;
  const num = parseFloat(cleanedValue);
  return Number.isNaN(num) ? undefined : num;
}

function parseToInt(value) {
  const num = parseInt(value, 10);
  return Number.isNaN(num) ? undefined : num;
}

function convertDateFormat(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const dateParts = dateStr.split('-');
  if (dateParts.length !== 3) return dateStr;
  const month = dateParts[0];
  const day = dateParts[1];
  const year = dateParts[2];
  return `${year}-${month}-${day}`;
}

export const payrixPayment = {
  namespaced: true,
  state: () => ({
    activeItemIndex: null,
    payrixPaymentData: null,
    payrixPaymentType: 'CREDIT',
    paymentContext: 'TAX', // 'TAX', 'OD', 'MISC', or 'MTG'
    odDepositData: null,
    taxCartContents: null,
    miscReceiptData: null,
    mtgReceiptData: null,
    isDialogOpen: false,
    paymentSuccessFlag: false,
    lastSavedPayment: null,
    transactionId: null,
    phone: null,
    email: null,
    cardholderName: null,
    paymentMethod: null,
    lastSavedPaymentItem: {},
    // One built taxccpayments record per successful charge, so a payment split across
    // multiple cards can create a CC record for EVERY card. The response singletons above
    // only ever hold the LAST charged card; this list keeps them all. Deduped by
    // transactionId; cleared after a successful save and when a fresh payment begins.
    capturedCCPayments: [],
  }),
  mutations: {
    setActiveItemIndex(state, idx) {
      state.activeItemIndex = idx;
    },
    setPayrixPaymentData(state, payload) {
      state.payrixPaymentData = payload;
    },
    setPayrixPaymentType(state, paymentType) {
      state.payrixPaymentType = paymentType;
    },
    setPaymentContext(state, context) {
      state.paymentContext = context;
    },
    setOdDepositData(state, data) {
      state.odDepositData = data;
    },
    setTaxCartContents(state, data) {
      state.taxCartContents = data;
    },
    setMiscReceiptData(state, data) {
      state.miscReceiptData = data;
    },
    setMtgReceiptData(state, data) {
      state.mtgReceiptData = data;
    },
    setDialogOpen(state, flag) {
      state.isDialogOpen = flag;
    },
    setPaymentSuccessFlag(state, { flag, transactionId }) {
      state.paymentSuccessFlag = flag;
      state.transactionId = transactionId;
    },
    setCardholderInfo(state, {
      phone, email, cardholderName, paymentMethod,
    }) {
      state.phone = phone;
      state.email = email;
      state.cardholderName = cardholderName;
      state.paymentMethod = paymentMethod;
    },
    setLastSavedPaymentItem(state, item) {
      state.lastSavedPaymentItem = item;
    },
    addCapturedCCPayment(state, record) {
      // Accumulate each charge's built CC record. Dedupe by the gateway transactionId so a
      // re-fired success watcher (or a re-entered save) can't create a duplicate row.
      if (!record || !record.transactionId) return;
      const alreadyCaptured = state.capturedCCPayments
        .some((existing) => existing.transactionId === record.transactionId);
      if (!alreadyCaptured) {
        state.capturedCCPayments = [...state.capturedCCPayments, record];
      }
    },
    clearCapturedCCPayments(state) {
      state.capturedCCPayments = [];
    },
  },
  actions: {
    setActiveItemIndex({ commit }, idx) {
      commit('setActiveItemIndex', idx);
    },
    setPayrixPaymentData({ commit }, data) {
      commit('setPayrixPaymentData', data);
    },
    setPayrixPaymentType({ commit }, paymentType) {
      commit('setPayrixPaymentType', paymentType);
    },
    setPaymentContext({ commit }, context) {
      commit('setPaymentContext', context);
    },
    setOdDepositData({ commit }, data) {
      commit('setOdDepositData', data);
    },
    setTaxCartContents({ commit }, data) {
      commit('setTaxCartContents', data);
    },
    setMiscReceiptData({ commit }, data) {
      commit('setMiscReceiptData', data);
    },
    setMtgReceiptData({ commit }, data) {
      commit('setMtgReceiptData', data);
    },
    toggleDialog({ commit }, flag) {
      commit('setDialogOpen', flag);
    },
    setPaymentSuccessFlag({ commit }, { flag, transactionId }) {
      commit('setPaymentSuccessFlag', { flag, transactionId });
    },
    setCardholderInfo({ commit }, {
      phone, email, cardholderName, paymentMethod,
    }) {
      commit('setCardholderInfo', {
        phone, email, cardholderName, paymentMethod,
      });
    },
    // this method also applies to eCheck payments
    setLastSavedPaymentItem({
      commit, rootState, rootGetters, state,
    }, rawItem) {
      // Handle empty rawItem (used for clearing state)
      if (!rawItem || Object.keys(rawItem).length === 0) {
        commit('setLastSavedPaymentItem', {});
        commit('clearCapturedCCPayments');
        return;
      }

      const paymentDetails = Array.isArray(rawItem.paymentDetails) ? rawItem.paymentDetails : [];
      const creditEcheckPayments = paymentDetails.filter((detail) => detail && (detail.paymentType === 'CREDIT' || detail.paymentType === 'ECHECK'));
      const activeDetail = state.activeItemIndex !== null
        && paymentDetails[state.activeItemIndex]
        && (paymentDetails[state.activeItemIndex].paymentType === 'CREDIT' || paymentDetails[state.activeItemIndex].paymentType === 'ECHECK')
        ? paymentDetails[state.activeItemIndex]
        : null;
      const detail0 = activeDetail
        || (creditEcheckPayments.length > 0 ? creditEcheckPayments[0] : {});

      // Validate critical data and collect errors
      const validationErrors = [];

      if (creditEcheckPayments.length === 0) {
        validationErrors.push('No CREDIT or ECHECK payment found in paymentDetails');
      }

      if (!state.transactionId) {
        validationErrors.push('Missing transactionId in store state');
      }

      if (!state.paymentMethod) {
        validationErrors.push('Missing paymentMethod in store state');
      }

      const amountPaid = parseToFloat(detail0.amount) || 0;
      if (amountPaid === 0) {
        validationErrors.push(`Invalid amountPaid: ${detail0.amount}`);
      }

      const taxDetails = rawItem.taxDetails || [];
      if (taxDetails.length === 0) {
        validationErrors.push('No taxDetails provided');
      }

      // Log warnings for missing but non-critical data
      if (!state.cardholderName) {
        console.warn('setLastSavedPaymentItem: Missing cardholderName in store state');
      }

      let merchantId = '';
      let merchantIdSource = '';
      if (state.paymentContext === 'MISC') {
        merchantIdSource = 'miscSettingItem.payrixMerchantId';
        merchantId = (rootState.SystemConfig
          && rootState.SystemConfig.miscSettingItem
          && rootState.SystemConfig.miscSettingItem.payrixMerchantId) || '';
      } else if (state.paymentContext === 'MTG') {
        merchantIdSource = 'mtgSettingItem.payrixMerchantId';
        merchantId = (rootState.SystemConfig
          && rootState.SystemConfig.mtgSettingItem
          && rootState.SystemConfig.mtgSettingItem.payrixMerchantId) || '';
      } else {
        merchantIdSource = 'paymentSettingItem.posMerchantId';
        merchantId = (rootState.SystemConfig
          && rootState.SystemConfig.paymentSettingItem
          && rootState.SystemConfig.paymentSettingItem.posMerchantId) || '';
      }

      if (!merchantId) {
        console.warn(`setLastSavedPaymentItem: Missing ${merchantIdSource} (paymentContext: ${state.paymentContext})`);
      }

      // If there are critical validation errors, log them and throw
      if (validationErrors.length > 0) {
        const errorMessage = `setLastSavedPaymentItem validation failed: ${validationErrors.join('; ')}`;
        console.error(errorMessage, {
          rawItem,
          storeState: {
            transactionId: state.transactionId,
            paymentMethod: state.paymentMethod,
            cardholderName: state.cardholderName,
          },
        });
        throw new Error(errorMessage);
      }

      const parsedItem = {
        _id: '',
        paymentDate: rootGetters.todaysDate, // this is the cc payment date
        systemDate: rawItem.payDate ? convertDateFormat(rawItem.payDate) : rootGetters.todaysDate,
        paidClearedDate: undefined,
        paymentNo: rawItem.paymentNumber || undefined,
        paymentGateway: 'PAYRIX',
        merchantId,
        lengthOfTaxDetails: taxDetails.length,
        maskedAccount: null,
        transactionId: state.transactionId,
        cardName: state.paymentMethod,
        amountPaid,
        status: 'OUTSTANDING',
        paymentType: detail0.paymentType || '',
        specialPaymentType: state.paymentMethod,
        modeOfPayment: 'POS',
        apiLoginKey: '',
        timeChanged: '',
        paidBy: state.cardholderName || '',
        emailAddress: typeof state.email !== 'undefined' ? state.email : '',
        // address fields are blank for payrix payments
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: undefined,
        county: '',
        phoneNo: typeof state.phone !== 'undefined' ? state.phone : '',
        chunkID: '',
        taxDetails: taxDetails.map((detail) => ({
          paymentInstallment: 'FINAL',
          taxYear: parseToInt(detail.taxYear) || null,
          taxId: parseToInt(detail.taxId) || undefined,
          detailsCount: parseToInt(detail.detailsCount),
          taxAmount: parseToFloat(detail.taxAmt),
          penalty: parseToFloat(detail.penaltyAmt),
          feeTotal: parseToFloat(detail.totalFees),
          total: parseToFloat(detail.total),
        })),
      };
      commit('setLastSavedPaymentItem', parsedItem);
      commit('addCapturedCCPayment', parsedItem);
    },
    clearCapturedCCPayments({ commit }) {
      commit('clearCapturedCCPayments');
    },
  },
  getters: {
    getPayrixPaymentData: (state) => state.payrixPaymentData,
    getTaxCartContents: (state) => state.taxCartContents,
    isDialogOpen: (state) => state.isDialogOpen,
    getPaymentSuccessFlag: (state) => state.paymentSuccessFlag,
    getLastSavedPaymentItem: (state) => state.lastSavedPaymentItem,
    getCapturedCCPayments: (state) => state.capturedCCPayments,
  },
};

export default payrixPayment;
